import './PartnerCreateOrEdit.scss';

import * as React from 'react';
import moment from 'moment-timezone';

import {
  Checkbox,
  CheckboxVisibility,
  CommandBarButton,
  DefaultButton,
  DetailsList,
  Dropdown,
  IColumn,
  IDropdownOption,
  IconButton,
  Label,
  MessageBar,
  MessageBarType,
  TextField,
  TooltipHost,
  Icon,
} from '@fluentui/react';
import { Partner, PartnerAddress } from '../../redux/on-site-manager/partners/types';

import { Clipboard } from '../core/Clipboard';
import { HealAPI } from '../../healApi';
import { OkCancelDialog } from '../core/OkCancelDialog';
import { OnSite } from '../../redux/on-site-manager/on-sites/types';
import { OnSiteCreateOrEditPanel } from './OnSiteCreateOrEditPanel';
import { PartnerAddressCreateOrEditPanel } from './PartnerAddressCreateOrEditPanel';
import { Payer } from '../../redux/claim/types';
import { ROUTES } from '../../config/routes';
import { ToastParams } from '../../redux/message/types';
import { PageHeader } from '../../components/core/PageHeader';
import { QRCodeDialog } from './QRCodeDialog';
import { NavigateFunction } from 'react-router-dom';

export interface OwnProps {
  params?: any;
  navigate?: NavigateFunction;
}

export interface StateProps {}

export interface DispatchProps {
  actions: {
    createPartner: (partner: Partner) => void;
    editPartner: (partner: Partner) => void;
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
    sendOnsiteEmail: (onSiteId: string, isPromo: boolean, isCashPay: boolean) => void;
    toast: (params: ToastParams) => void;
  };
}

type PartnerCreateOrEditProps = OwnProps & StateProps & DispatchProps;

export interface PartnerCreateOrEditState {
  partner: Partner;
  isNew: boolean;
  isSaving: boolean;
  isLoading: boolean;
  imageError?: string;
  editCampus?: PartnerAddress;
  editOnSite?: OnSite;
  error?: string;
  payers: IDropdownOption[];
  campusColumns: IColumn[];
  onsites: OnSite[];
  onsiteColumns: IColumn[];
}

function createNewPartner(): Partner {
  return {
    companyInfo: '',
    companyUrl: '',
    name: '',
    logoUrl: '',
    allowPostApptEmail: false,
    allowReceiptEmail: true,
    allowWelcomeEmail: true,
    allowReminderEmail: true,
    addresses: [],
  };
}

function createNewPartnerAddress(): PartnerAddress {
  return {
    bookingUrl: '',
    contactEmail: '',
    contactName: '',
    contactPhone: '',
    name: '',
  };
}

function createNewOnSite(): OnSite {
  return {} as any;
}

export class PartnerCreateOrEdit extends React.Component<PartnerCreateOrEditProps, PartnerCreateOrEditState> {
  private fileInput: HTMLInputElement | null = null;

  constructor(props: PartnerCreateOrEditProps) {
    super(props);
    this.state = {
      partner: createNewPartner(),
      isNew: !this.props.params || !this.props.params.id,
      isSaving: false,
      isLoading: false,
      payers: [],
      onsites: [],
      campusColumns: this.buildCampusColumns(),
      onsiteColumns: this.buildOnsiteColumns(),
    };
  }

  public componentDidMount() {
    this.props.actions.onPageDidMount('PartnerCreateOrEdit');
    const promises: Promise<any>[] = [];
    if (this.props.params && this.props.params.id) {
      const partnerId = this.props.params.id;
      this.setState({ isLoading: true });
      const p1 = HealAPI.fetchPartner(partnerId)
        .then((response) => {
          if (response.ok && response.data) {
            this.setState({ partner: response.data });
          } else {
            this.setState({ error: 'The partner does not exist.' });
          }
        })
        .catch(() => {
          this.setState({ error: 'There was an error connecting to the server.' });
        });
      promises.push(p1);

      const p2 = HealAPI.fetchAllOnSites().then((response) => {
        if (response.ok && response.data) {
          this.setState({
            onsites: response.data.filter((o) => o.partnerId === partnerId).reverse(),
          });
        } else {
          this.setState({ error: 'There was an error retrieving the onsites for this partner.' });
        }
      });
      promises.push(p2);
    }

    const p3 = HealAPI.fetchPayers().then((response) => {
      if (response.ok && response.data) {
        const payers = response.data.map((payer: Payer) => {
          return {
            key: payer.id,
            text: payer.name,
          };
        });
        payers.unshift({
          key: '',
          text: 'No Insurance',
        });
        this.setState({ payers });
      } else {
        this.setState({ error: 'There was an error retrieving the payers from the server.' });
      }
    });
    promises.push(p3);

    Promise.all(promises).then(() => this.setState({ isLoading: false }));
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('PartnerCreateOrEdit');
  }

  public render(): JSX.Element {
    if (this.state.error) {
      return (
        <div style={{ marginTop: 20 }}>
          <MessageBar messageBarType={MessageBarType.error}>{this.state.error}</MessageBar>
        </div>
      );
    }

    return (
      <div>
        <PageHeader title={this.state.isNew ? 'Create Partner' : 'Edit Partner'} isLoading={this.state.isLoading} />
        {this.renderPartnerDetails()}
        {this.renderCampuses()}
        {this.renderOnsites()}
        {this.state.editCampus && <PartnerAddressCreateOrEditPanel address={this.state.editCampus} partner={this.state.partner} close={this.onCampusAdded} />}
        {this.state.editOnSite && <OnSiteCreateOrEditPanel onSite={this.state.editOnSite} partner={this.state.partner} onClose={this.onOnSiteAdded} />}
      </div>
    );
  }

  private renderPartnerDetails(): JSX.Element {
    return (
      <div style={{ maxWidth: 1000 }} className="partner-details partner-row flex flex-column ms-depth-8">
        <div className="flex flex-row justify-space-between">
          <Label>Partner Details</Label>
          <div className="flex" style={{ height: 32 }}>
            <CommandBarButton iconProps={{ iconName: 'Save' }} text={this.state.isNew ? 'Create' : 'Save'} onClick={this.savePartner} disabled={!this.isValid()} />
          </div>
        </div>
        <div className="">
          <div className="partner-row flex flex-row">
            <div className="flex-grow flex-no-basis" style={{ marginRight: 10 }}>
              <TextField
                label="Partner name"
                required={true}
                value={this.state.partner.name}
                disabled={!!this.state.partner.id}
                onChange={(ev, value) => this.setState({ partner: { ...this.state.partner, name: value! } })}
                data-tid="inp_partnerName"
              />
            </div>
            <div className="flex-grow flex-no-basis" style={{ marginRight: 10 }}>
              <Dropdown
                label="Payer"
                selectedKey={this.state.partner.eligiblePayerCode || ''}
                options={this.state.payers.length === 0 ? [{ key: '', text: 'Fetching...' }] : this.state.payers}
                disabled={this.state.payers.length === 0}
                onChange={(ev, opt) =>
                  opt &&
                  this.setState({
                    partner: { ...this.state.partner, eligiblePayerCode: String(opt.key) },
                  })
                }
                data-tid="dd_payer"
              />
            </div>
            <div className="flex-grow flex-no-basis">
              <div className="flex flex-column flex-no-basis">
                <div className="flex flex-center" data-tid="inp_logo">
                  <Label required={true}>Logo</Label>
                  {this.state.partner.logoUrl && (
                    <IconButton
                      style={{ height: 28 }}
                      iconProps={{ iconName: 'NavigateExternalInline' }}
                      title="Open in new tab"
                      ariaLabel="Open in new tab"
                      onClick={() => window.open(this.state.partner.logoUrl, '_blank')}
                    />
                  )}
                </div>
                <div className="flex flex-row">
                  <div className="flex-grow flex-no-basis">
                    <TextField value={this.state.partner.logoUrl} readOnly={true} errorMessage={this.state.imageError} />
                  </div>
                  <div className="flex">
                    <input value={undefined} ref={(f) => (this.fileInput = f)} type="file" accept="image/*" style={{ display: 'none' }} onChange={this.onFileChange} />
                    <DefaultButton text="Browse..." onClick={() => this.fileInput!.click()} allowDisabledFocus={true} data-tid="btn_browse" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="partner-row flex flex-row">
            <div className="flex-grow flex-no-basis">
              <TextField
                label="Partner Contact & Information"
                value={this.state.partner.companyInfo}
                multiline={true}
                rows={5}
                onChange={(ev, value) => this.setState({ partner: { ...this.state.partner, companyInfo: value! } })}
                data-tid="inp_partnerContactInformation"
              />
            </div>
          </div>

          <div className="partner-row flex flex-column">
            <Label>Communication preferences</Label>
            <div className="flex flex-row" style={{ paddingTop: 6 }}>
              <Checkbox
                className="email-checkbox"
                label="Registration"
                checked={this.state.partner.allowWelcomeEmail}
                onChange={(event, value) =>
                  this.setState({
                    partner: { ...this.state.partner, allowWelcomeEmail: value as boolean },
                  })
                }
                data-tid="btn_registration"
              />
              <Checkbox
                className="email-checkbox"
                label="Appointment reminders"
                checked={this.state.partner.allowReminderEmail}
                onChange={(event, value) =>
                  this.setState({
                    partner: { ...this.state.partner, allowReminderEmail: value as boolean },
                  })
                }
                data-tid="btn_appointmentReminders"
              />
              <Checkbox
                className="email-checkbox"
                label="Receipt"
                checked={this.state.partner.allowReceiptEmail}
                onChange={(event, value) =>
                  this.setState({
                    partner: { ...this.state.partner, allowReceiptEmail: value as boolean },
                  })
                }
                data-tid="btn_receipt"
              />
            </div>
          </div>
          <div className="partner-row flex flex-row">
            <div className="flex-grow flex-no-basis" style={{ marginRight: 10 }}>
              <TextField
                label="Broker name (Optional)"
                value={this.state.partner.brokerName || undefined}
                onChange={(ev, value) => this.setState({ partner: { ...this.state.partner, brokerName: value! } })}
                data-tid="inp_brokerName"
              />
            </div>
            <div className="flex-grow flex-no-basis" style={{ marginRight: 10 }}>
              <TextField
                label="Broker email (Optional)"
                value={this.state.partner.brokerEmail || undefined}
                onChange={(ev, value) => this.setState({ partner: { ...this.state.partner, brokerEmail: value! } })}
                data-tid="inp_brokerEmail"
              />
            </div>
            <div className="flex-grow flex-no-basis">
              <TextField
                label="Broker phone (Optional)"
                value={this.state.partner.brokerPhone || undefined}
                onChange={(ev, value) => this.setState({ partner: { ...this.state.partner, brokerPhone: value! } })}
                data-tid="inp_brokerPhone"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderCampuses(): JSX.Element | null {
    if (this.state.isNew) {
      return null;
    }

    return (
      <div className="partner-details partner-row flex flex-column ms-depth-8">
        <div className="flex flex-row justify-space-between">
          <Label>{`Campuses (${this.state.partner.addresses.length})`}</Label>
          <div className="flex" style={{ height: 32 }}>
            <CommandBarButton iconProps={{ iconName: 'Add' }} text="Campus" onClick={this.onAddCampusClick} />
          </div>
        </div>
        <DetailsList
          compact={true}
          className="details-list-wrapper"
          checkboxVisibility={CheckboxVisibility.hidden}
          items={this.state.partner.addresses}
          setKey="set"
          columns={this.state.campusColumns}
        />
      </div>
    );
  }

  private renderOnsites(): JSX.Element | null {
    if (this.state.isNew) {
      return null;
    }

    return (
      <div className="partner-details partner-row flex flex-column ms-depth-8">
        <div className="flex flex-row justify-space-between">
          <Label>{`Onsites (${this.state.onsites.length})`}</Label>
          <div className="flex" style={{ height: 32 }}>
            <CommandBarButton iconProps={{ iconName: 'Add' }} text="On-site" onClick={this.onAddOnSiteClick} />
          </div>
        </div>
        <DetailsList
          compact={true}
          className="details-list-wrapper"
          checkboxVisibility={CheckboxVisibility.hidden}
          items={this.state.onsites}
          setKey="set"
          columns={this.state.onsiteColumns}
        />
      </div>
    );
  }

  private onAddOnSiteClick = () => {
    const editOnSite = createNewOnSite();
    this.setState({ editOnSite });
  };

  private onOnSiteAdded = (onSite?: OnSite) => {
    this.setState({ editOnSite: undefined });
    if (onSite) {
      const index = this.state.onsites.findIndex((o) => o.onsiteId === onSite.onsiteId);
      if (index >= 0) {
        this.props.actions.toast({
          type: 'success',
          title: 'Updated',
          content: 'The on-site was successfully updated.',
        });
        const onsites = this.state.onsites.slice();
        onsites[index] = onSite;
        this.setState({ onsites });
      } else {
        this.props.actions.toast({
          type: 'success',
          title: 'Created',
          content: 'The on-site was successfully created.',
        });
        this.setState({
          onsites: [onSite].concat(this.state.onsites),
        });
      }
    }
  };

  private onAddCampusClick = () => {
    const editCampus = createNewPartnerAddress();
    this.setState({ editCampus });
  };

  private onOnSiteDeleted = (onSiteId?: string) => {
    const index = this.state.onsites.findIndex((o) => o.onsiteId === onSiteId);
    if (index >= 0) {
      this.props.actions.toast({
        type: 'success',
        title: 'Success',
        content: 'On Site event deleted successfully',
      });
      const onsites = [...this.state.onsites];
      onsites.splice(index, 1);
      this.setState({ onsites });
    }
  };

  private onCampusAdded = (partnerAddress?: PartnerAddress) => {
    this.setState({ editCampus: undefined });
    if (partnerAddress) {
      const index = this.state.partner.addresses.findIndex((a) => a.id === partnerAddress.id);
      if (index >= 0) {
        this.props.actions.toast({
          type: 'success',
          title: 'Updated',
          content: 'The campus was successfully updated.',
        });
        const addresses = this.state.partner.addresses.slice();
        addresses[index] = partnerAddress;
        this.setState({
          partner: {
            ...this.state.partner,
            addresses,
          },
        });
      } else {
        this.props.actions.toast({
          type: 'success',
          title: 'Created',
          content: 'The campus was successfully created.',
        });
        this.setState({
          partner: {
            ...this.state.partner,
            addresses: this.state.partner.addresses.slice().concat(partnerAddress),
          },
        });
      }
    }
  };

  private onRemoveLocation(index: number) {
    const newAddresses = this.state.partner.addresses.slice();
    newAddresses.splice(index, 1);
    this.setState({
      partner: {
        ...this.state.partner,
        addresses: newAddresses,
      },
    });
  }

  private onSendOnsiteEmail(onSiteId: string, isPromo: boolean, isCashPay: boolean) {
    HealAPI.sendOnSiteEmail(onSiteId, isPromo, isCashPay).then((response) => {
      if (response.ok) {
        const index = this.state.onsites.findIndex((o) => o.onsiteId === onSiteId);
        const onsites = [...this.state.onsites];
        onsites[index].isCashPayEmailSent = onsites[index].isCashPayEmailSent || isCashPay;
        onsites[index].isPromoEmailSent = onsites[index].isPromoEmailSent || isPromo;
        onsites[index].isEmailSent = onsites[index].isEmailSent || (!isPromo && !isCashPay);
        this.setState({ onsites });
        this.props.actions.toast({
          type: 'success',
          title: 'Success',
          content: 'Email sent successfully to ' + response.data.email,
        });
      } else {
        const content = (response.data || {}).description || 'Something went wrong.';
        this.props.actions.toast({ type: 'error', content });
      }
    });
  }

  private onFileChange = (ev: React.SyntheticEvent<HTMLInputElement>) => {
    const files = ev.currentTarget.files;
    if (!files) {
      return;
    }

    const newFormData = new FormData();
    newFormData.append('logo', files[0]);

    if (this.props.actions && files[0].size < 5000000) {
      HealAPI.uploadImage(newFormData).then((response) => {
        if (response.ok && response.data) {
          this.setState({
            partner: {
              ...this.state.partner,
              logoUrl: response.data.url as string,
            },
            imageError: undefined,
          });
        } else {
          this.setState({
            imageError: 'Something went wrong with your upload. Please try another file',
          });
        }
      });
    } else {
      this.setState({
        imageError: 'The file chosen is over 5MB. Please use another file.',
      });
    }
  };

  private isValid(): boolean {
    const { partner } = this.state;

    // Not values or all values...
    const isBrokerValid = (!partner.brokerEmail && !partner.brokerName && !partner.brokerPhone) || (!!partner.brokerEmail && !!partner.brokerName && !!partner.brokerPhone);

    return isBrokerValid && !!partner.name && !!partner.logoUrl;
  }

  private savePartner = () => {
    if (this.isValid()) {
      const { partner, isNew } = this.state;
      this.setState({ isSaving: true });
      if (isNew) {
        HealAPI.createPartner(partner).then((response) => {
          if (response.ok && response.data) {
            this.props.actions.toast({
              type: 'success',
              title: 'Created',
              content: 'The partner was successfully created.',
            });
            this.props.navigate!(ROUTES.onSiteManagerEditPartner.replace(':id', response.data.id!), { replace: true });
            window.location.reload();
          } else {
            const content = (response.data || {}).description || 'Something went wrong.';
            this.props.actions.toast({ type: 'error', content });
          }
        });
      } else {
        HealAPI.editPartner(partner).then((response) => {
          if (response.ok) {
            this.props.actions.toast({
              type: 'success',
              title: 'Updated',
              content: 'The partner was successfully updated.',
            });
            this.setState({ isSaving: false });
          } else {
            const content = (response.data || {}).description || 'Something went wrong.';
            this.props.actions.toast({ type: 'error', content });
          }
        });
      }
    }
  };

  private buildCampusColumns(): IColumn[] {
    return [
      {
        key: 'name',
        name: 'Name',
        fieldName: 'name',
        minWidth: 80,
        maxWidth: 120,
        isResizable: true,
      },
      {
        key: 'fullAddress',
        name: 'Location',
        fieldName: 'fullAddress',
        minWidth: 80,
        maxWidth: 200,
        isResizable: true,
      },
      {
        key: 'qrcode',
        name: 'QR Code',
        fieldName: '',
        minWidth: 60,
        maxWidth: 60,
        onRender: (item: PartnerAddress) => {
          const name = `${this.state.partner.name} ${item.city} qr code`;
          return <IconButton iconProps={{ iconName: 'QRCode' }} title="Download QRCode" onClick={() => QRCodeDialog.show({ title: name, url: item.bookingUrl })} />;
        },
      },
      {
        key: 'landing',
        name: 'Landing',
        fieldName: '',
        minWidth: 60,
        maxWidth: 60,
        onRender: (item: PartnerAddress) => {
          const link = `https://www.heal.com/partner?campus=${item.publicCode}`;
          return (
            <Clipboard className="cursor-copy" content={link} message="Landing page link copied to clipboard">
              <TooltipHost content={link}>
                <IconButton iconProps={{ iconName: 'Copy' }} />
              </TooltipHost>
            </Clipboard>
          );
        },
      },
      {
        key: 'booking',
        name: 'Booking',
        fieldName: '',
        minWidth: 60,
        maxWidth: 60,
        onRender: (item: PartnerAddress) => {
          return (
            <Clipboard className="cursor-copy" content={item.bookingUrl} message="Booking link copied to clipboard">
              <TooltipHost content={item.bookingUrl}>
                <IconButton iconProps={{ iconName: 'Copy' }} />
              </TooltipHost>
            </Clipboard>
          );
        },
      },
      {
        key: 'cName',
        name: 'Contact name',
        fieldName: 'contactName',
        minWidth: 80,
        maxWidth: 120,
      },
      {
        key: 'cEmail',
        name: 'Contact email',
        fieldName: 'contactEmail',
        minWidth: 80,
        maxWidth: 120,
      },
      {
        key: 'cPhone',
        name: 'Contact phone',
        fieldName: 'contactPhone',
        minWidth: 80,
        maxWidth: 120,
      },
      {
        key: 'action',
        name: '',
        fieldName: '',
        minWidth: 50,
        maxWidth: 50,
        onRender: (item: PartnerAddress) => {
          return (
            <IconButton
              className="hidable-menu-button"
              allowDisabledFocus={true}
              iconProps={{ iconName: 'ContextMenu' }}
              menuProps={{
                items: [
                  {
                    key: 'edit',
                    text: 'Edit',
                    iconProps: { iconName: 'Edit' },
                    onClick: () => {
                      this.setState({ editCampus: item });
                    },
                  },
                ],
              }}
            />
          );
        },
      },
    ];
  }

  private buildOnsiteColumns(): IColumn[] {
    return [
      {
        key: 'name',
        name: 'Name',
        fieldName: 'name',
        minWidth: 80,
        maxWidth: 120,
        isResizable: true,
      },
      {
        key: 'room',
        name: 'Room',
        fieldName: 'room',
        minWidth: 80,
        maxWidth: 120,
        isResizable: true,
      },
      {
        key: 'date',
        name: 'Date',
        fieldName: '',
        minWidth: 80,
        maxWidth: 120,
        onRender: (item: OnSite) => {
          return moment(item.startDateTime).format('M/D/YYYY');
        },
      },
      {
        key: 'time',
        name: 'Time',
        fieldName: '',
        minWidth: 80,
        maxWidth: 120,
        isResizable: true,
        onRender: (item: OnSite) => {
          return `${moment(item.startDateTime).format('h:mm A')} - ${moment(item.endDateTime).format('h:mm A')}`;
        },
      },
      {
        key: 'breakTime',
        name: 'Break Time',
        fieldName: '',
        minWidth: 110,
        maxWidth: 110,
        onRender: (item: OnSite) => {
          return <div>{item.break ? moment(item.break.startTime, 'HH:mm').format('h:mm A') + ' - ' + moment(item.break.endTime, 'HH:mm').format('h:mm A') : 'No Break'}</div>;
        },
      },
      {
        key: 'serviceName',
        name: 'Service',
        fieldName: 'serviceName',
        minWidth: 80,
        maxWidth: 120,
      },
      {
        key: 'streetAddress',
        name: 'Campus',
        fieldName: '',
        minWidth: 80,
        maxWidth: 180,
        isResizable: true,
        onRender: (item: OnSite) => {
          if (this.state.partner.addresses && this.state.partner.addresses.length > 0) {
            const address = this.state.partner.addresses.find((a) => a.id === item.partnerAddressId);
            if (address) {
              return address.streetAddress;
            }
          }
          return null;
        },
      },
      {
        key: 'totalAppointments',
        name: 'Capacity',
        fieldName: 'totalAppointments',
        minWidth: 50,
        maxWidth: 50,
      },
      {
        key: 'appointmentsBooked',
        name: 'Booked',
        fieldName: 'appointmentsBooked',
        minWidth: 50,
        maxWidth: 50,
        onRender: (item: OnSite) => {
          let bgColor = 'transparent';
          if (item.appointmentsBooked > 0.7 * item.totalAppointments && item.appointmentsBooked < 0.9 * item.totalAppointments) {
            bgColor = '#83ff82';
          } else if (item.appointmentsBooked > 0.9 * item.totalAppointments) {
            bgColor = '#f5adad';
          } else if (item.appointmentsBooked < 0.2 * item.totalAppointments) {
            bgColor = '#ffc770';
          }
          return <div style={{ backgroundColor: bgColor }}>{item.appointmentsBooked}</div>;
        },
      },
      {
        key: 'appointmentsFinished',
        name: 'Completed',
        fieldName: 'appointmentsFinished',
        minWidth: 60,
        maxWidth: 60,
      },
      {
        key: 'appointmentsNoShow',
        name: 'No show',
        fieldName: 'appointmentsNoShow',
        minWidth: 50,
        maxWidth: 50,
      },
      {
        key: 'email',
        name: 'Email',
        fieldName: '',
        minWidth: 40,
        maxWidth: 40,
        onRender: (item: OnSite) => {
          const content: JSX.Element[] = [];
          if (item.isEmailSent) {
            content.push(<div key="generic">Generic email sent</div>);
          }
          if (item.isPromoEmailSent) {
            content.push(<div key="promo">Promo email sent</div>);
          }
          if (item.isCashPayEmailSent) {
            content.push(<div key="cash">Cash pay email sent</div>);
          }
          if (content.length > 0) {
            return (
              <TooltipHost tooltipProps={{ onRenderContent: () => <div>{content}</div> }}>
                <Icon iconName="Accept" />
                <span>{'' + content.length}</span>
              </TooltipHost>
            );
          }
          return null;
        },
      },
      {
        key: 'action',
        name: '',
        fieldName: '',
        minWidth: 50,
        maxWidth: 50,
        onRender: (item: OnSite) => {
          return (
            <IconButton
              className="hidable-menu-button"
              allowDisabledFocus={true}
              iconProps={{ iconName: 'ContextMenu' }}
              menuProps={{
                items: [
                  {
                    key: 'edit',
                    text: 'Edit',
                    iconProps: { iconName: 'Edit' },
                    onClick: () => {
                      this.setState({ editOnSite: item });
                    },
                  },
                  {
                    key: 'booking',
                    text: 'Send booking email',
                    iconProps: { iconName: 'send' },
                    // disabled: item.isEmailSent,
                    onClick: () => {
                      OkCancelDialog.show({
                        title: 'Send Booking Email',
                        subText: 'This will send booking email for ' + item.name + ' to onsite@heal.com. Are you sure?',
                        onOk: () => this.onSendOnsiteEmail(item.onsiteId, false, false),
                      });
                    },
                  },
                  {
                    key: 'promo',
                    text: 'Send promo code email',
                    iconProps: { iconName: 'send' },
                    // disabled: item.isPromoEmailSent,
                    onClick: () => {
                      OkCancelDialog.show({
                        title: 'Send Promo Code Email',
                        subText: 'This will send promo code email for ' + item.name + ' to onsite@heal.com. Are you sure?',
                        onOk: () => this.onSendOnsiteEmail(item.onsiteId, true, false),
                      });
                    },
                  },
                  {
                    key: 'cash',
                    text: 'Send cash pay email',
                    iconProps: { iconName: 'send' },
                    // disabled: item.isCashPayEmailSent,
                    onClick: () => {
                      OkCancelDialog.show({
                        title: 'Send Cash Pay Email',
                        subText: 'This will send cash pay email for ' + item.name + ' to onsite@heal.com. Are you sure?',
                        onOk: () => this.onSendOnsiteEmail(item.onsiteId, false, true),
                      });
                    },
                  },
                  {
                    key: 'delete',
                    text: 'Delete',
                    iconProps: { iconName: 'Delete' },
                    disabled: item.appointmentsBooked !== 0,
                    onClick: () => {
                      OkCancelDialog.show({
                        title: 'Delete Onsite',
                        subText: 'This will delete ' + item.name + '. Are you sure?',
                        onOk: () => {
                          HealAPI.deleteOnSiteEvent(item.onsiteId).then((response) => {
                            if (response.ok) {
                              this.onOnSiteDeleted(item.onsiteId);
                            } else {
                              const content = (response.data || {}).description || 'Something went wrong.';
                              this.props.actions.toast({ type: 'error', content });
                            }
                          });
                        },
                      });
                    },
                  },
                ],
              }}
            />
          );
        },
      },
    ];
  }
}
